import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AppsIcon from "@mui/icons-material/Apps";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";

import { useShoppingCart } from "src/context/ShoppingCartContext";
import { logout } from "src/services/auth";
import { helpBaseUrl, helpPageFor, helpUrl } from "src/services/help";
import Button from "../../components/Button";
import Logo from "../../components/Logo";
import LogoutButton from "../logout-button";
import useCrew from "../../hooks/useCrew";
import useFeatureFlag, { FEATURE_FLAGS } from "../../hooks/useFeatureFlag";
import useUser from "../../hooks/useUser";
import NotificationSettingsDialog from "../notification-settings/NotificationSettingsDialog";
import CrewNotificationBell, { CrewNotificationsProvider } from "../notifications/CrewNotificationBell";
import Wilco from "../wilco";
import CompanySelector from "./CompanySelector";
import HeaderLanguageSelect from "./HeaderLanguageSelect";
import SettingsDialog from "./SettingsDialog";
import { StyledList } from "./styles";
import Hamburger from "../../assets/icon/hamburger.png";
import User_Icon from "../../assets/icon/profile.png";
import "./index.scss";

// The Cockpit's account page is the user's profile (name, email, correspondence language, …).
const profileUrl = () => `${process.env.REACT_APP_DASHBOARD_URI}/account`;

const Header = ({ menuOpen, setMenuOpen }) => {
  const { t } = useTranslation();

  const { crew } = useCrew();
  const user = useUser();
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [hasShoppingItems, setHasShoppingItems] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { items: shoppingCartItems } = useShoppingCart();
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] = useState(false);
  const showNotificationSettings = useFeatureFlag(FEATURE_FLAGS.NOTIFICATION_SETTINGS);

  useEffect(() => {
    setHasShoppingItems(shoppingCartItems?.length > 0);
  }, [shoppingCartItems]);

  const openSettingsDialog = () => {
    setIsSettingsMenuOpen(false);
    setIsSettingsDialogOpen(true);
  };

  const hasHelp = Boolean(helpBaseUrl());
  // Opens the tutorial chapter for the page the user is on, in a new tab.
  const openHelp = () => window.open(helpUrl(helpPageFor(location.pathname)), "_blank", "noopener");

  const openNotificationSettings = () => {
    setIsSettingsMenuOpen(false);
    setIsNotificationSettingsOpen(true);
  };

  // For Tab Header
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <nav>
      <div className="logo">
        <Link to="/">
          <Logo logoUrl={crew.logo} crewName={crew.name} />
        </Link>
      </div>
      {/* For Mobile and tab start */}
      <div className="mobile-item hamburger-menu" onClick={() => setMenuOpen(!menuOpen)}>
        <img src={Hamburger} alt="icon" />
      </div>
      <div className="center-part mobile-item mobile-item-logo">
        <Link to="/">
          <Logo logoUrl={crew.logo} crewName={crew.name} />
        </Link>
      </div>
      {/* For Mobile and tab  end*/}
      <div className="info-block-wrapper">
        <CompanySelector />
        <Divider className="divider" orientation={"vertical"} flexItem={true} />
        <div className="infoblock">
          <div className="infoblock-column">
            <label>{t("user")}</label>
            <p>{user.fullname}</p>
          </div>
          <Button
            title="Show settings"
            icon={<SettingsIcon />}
            style={{ width: 30, height: 30 }}
            onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
          />

          {isSettingsMenuOpen && (
            <ClickAwayListener onClickAway={() => setIsSettingsMenuOpen(false)}>
              <div className="option-list">
                <StyledList className="profile-menu">
                  <HeaderLanguageSelect />
                  <ListItemButton className="option-list-item" component="a" href={profileUrl()}>
                    <ListItemText primary={t("header_settings.profile")} />
                  </ListItemButton>
                  <ListItemButton className="option-list-item" onClick={openSettingsDialog}>
                    <ListItemText primary={t("header_settings.settings")} />
                  </ListItemButton>
                  {showNotificationSettings && (
                    <ListItemButton className="option-list-item" onClick={openNotificationSettings}>
                      <ListItemText primary={t("header_settings.notification_settings")} />
                    </ListItemButton>
                  )}
                  {/* Help opens the table of contents, which the tutorial build always writes to index.html */}
                  {hasHelp && (
                    <ListItemButton
                      className="option-list-item"
                      component="a"
                      href={helpUrl("index")}
                      target="_blank"
                      rel="noopener"
                      onClick={() => setIsSettingsMenuOpen(false)}
                    >
                      <ListItemText primary={t("header_settings.help")} />
                    </ListItemButton>
                  )}
                </StyledList>
              </div>
            </ClickAwayListener>
          )}
        </div>
      </div>
      <div className="right row">
        <Divider className="divider" orientation={"vertical"} flexItem={true} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {hasHelp && <Button title={t("header.help_page")} icon={<HelpOutlineIcon />} onClick={openHelp} />}
          <CrewNotificationBell placement="desktop" />
          <Button
            icon={
              <Badge color="secondary" variant="dot" invisible={!hasShoppingItems}>
                <ShoppingCartIcon color={hasShoppingItems ? "primary" : "disabled"} />
              </Badge>
            }
            onClick={() => history.push(`/crew/${crew.id}/cart`)}
          />
        </Box>
        <LogoutButton />

        <Divider className="divider" orientation={"vertical"} flexItem={true} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            title={t("header.back_to_cockpit")}
            iconSize="medium"
            icon={<AppsIcon />}
            onClick={() => (window.location.href = process.env.REACT_APP_DASHBOARD_URI)}
          />
        </Box>
      </div>

      {/* For Mobile and tab  */}
      <div className="mobile-item">
        <div>
          <CompanySelector mobile />
        </div>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* 24px like the cart and profile icons next to it (fontSize "medium" is 1.5rem, 21px on CO's 14px root) */}
          {hasHelp && (
            <Button
              title={t("header.help_page")}
              icon={<HelpOutlineIcon sx={{ fontSize: 24 }} />}
              iconSize="medium"
              onClick={openHelp}
            />
          )}
          <CrewNotificationBell placement="mobile" />
          <Button
            icon={
              <Badge color="secondary" variant="dot" invisible={!hasShoppingItems}>
                <svg width={24} height={24} fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.5 7.67V6.7c0-2.25 1.81-4.46 4.06-4.67a4.5 4.5 0 0 1 4.94 4.48v1.38M9 22h6c4.02 0 4.74-1.61 4.95-3.57l.75-6C20.97 9.99 20.27 8 16 8H8c-4.27 0-4.97 1.99-4.7 4.43l.75 6C4.26 20.39 4.98 22 9 22Z"
                    stroke={hasShoppingItems ? "#151B28" : "rgba(0, 0, 0, 0.26)"}
                    strokeWidth={1.5}
                    strokeMiterlimit={10}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.495 12h.01M8.495 12h.008"
                    stroke={hasShoppingItems ? "#151B28" : "rgba(0, 0, 0, 0.26)"}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Badge>
            }
            onClick={() => history.push(`/crew/${crew.id}/cart`)}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", textAlign: "center" }} className="profile-box">
          <Tooltip title="Profile">
            <IconButton
              className="m-btn"
              onClick={handleClick}
              size="small"
              sx={{}}
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
            >
              <img src={User_Icon} alt="icon" />
            </IconButton>
          </Tooltip>
        </Box>
        <Menu
          className="profile-item"
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {/* The MenuItems own the actions so Enter works; their content has no handlers of its own,
              which would fire a second time on a mouse click. */}
          <HeaderLanguageSelect variant="menu" />
          <MenuItem component="a" href={profileUrl()}>
            <div className="profile-menu">{t("header_settings.profile")}</div>
          </MenuItem>
          <MenuItem onClick={openSettingsDialog}>
            <div className="profile-menu">{t("header_settings.settings")}</div>
          </MenuItem>
          {showNotificationSettings && (
            <MenuItem onClick={openNotificationSettings}>
              <div className="profile-menu">{t("header_settings.notification_settings")}</div>
            </MenuItem>
          )}
          {hasHelp && (
            <MenuItem component="a" href={helpUrl("index")} target="_blank" rel="noopener">
              <div className="profile-menu">{t("header_settings.help")}</div>
            </MenuItem>
          )}
          <MenuItem onClick={logout}>
            <Box className="logout">
              <ExitToAppIcon />
              <Box component="span" sx={{ ml: 1 }}>
                {t("logout")}
              </Box>
            </Box>
          </MenuItem>
        </Menu>
      </div>
      <SettingsDialog open={isSettingsDialogOpen} onClose={() => setIsSettingsDialogOpen(false)} />
      <NotificationSettingsDialog
        open={isNotificationSettingsOpen}
        onClose={() => setIsNotificationSettingsOpen(false)}
      />
    </nav>
  );
};

// One notifications provider for both bells (desktop and mobile).
const HeaderWithNotifications = (props) => (
  <CrewNotificationsProvider>
    <Header {...props} />
    <Wilco />
  </CrewNotificationsProvider>
);

export default HeaderWithNotifications;
